import { useEffect } from "react";

/**
 * Keyboard-shortcut binding. Listens on {@code window} (capture phase) so
 * the binding survives focus moving between page content elements.
 *
 * <p>Three conventions:
 *
 * <ul>
 *   <li><strong>Ignore typing surfaces.</strong> If the event target is an
 *       {@code <input>} / {@code <textarea>} / {@code [contenteditable]},
 *       single-character shortcuts are skipped (so typing "n" in a
 *       patient-name field does NOT trigger the global "n = new"
 *       shortcut). Combo shortcuts with a modifier still fire.
 *   <li><strong>Modal-aware.</strong> If a {@code .modal-backdrop} is
 *       visible in the DOM, shortcuts are suppressed (modals own their
 *       own keys, e.g. Esc to close).
 *   <li><strong>Cross-platform Cmd/Ctrl.</strong> Use {@code mod: true} —
 *       resolves to {@code metaKey} on macOS, {@code ctrlKey} elsewhere.
 *       Distinct shortcuts can still pin one or the other explicitly.
 * </ul>
 *
 * <p>Browser-reserved combos to avoid: Cmd/Ctrl + N (new window), Cmd/Ctrl
 * + T (new tab), Cmd/Ctrl + W (close tab), Cmd/Ctrl + R (reload).
 */
export interface ShortcutSpec {
  /** Lowercase key value, e.g. "k", "/", "?", "arrowleft", "enter", "escape". */
  key: string;
  /** True ⇒ Cmd on macOS, Ctrl elsewhere. */
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Run the handler even when the focus is in a text input. */
  allowInInput?: boolean;
  /** Run the handler even when a modal backdrop is showing. */
  allowInModal?: boolean;
}

export function useKeyboardShortcut(
  spec: ShortcutSpec,
  handler: (e: KeyboardEvent) => void,
  deps: React.DependencyList = []
): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // e.key is undefined for some events (autofill / synthetic keydowns); guard it —
      // this is a global listener, so an unguarded .toLowerCase() throws app-wide.
      if (!e.key || !spec.key) return;
      if (e.key.toLowerCase() !== spec.key.toLowerCase()) return;

      const mod = spec.mod ?? false;
      const usedMod = e.metaKey || e.ctrlKey;
      if (mod !== usedMod) return;

      if ((spec.shift ?? false) !== e.shiftKey) return;
      if ((spec.alt ?? false) !== e.altKey) return;

      // Composing IME (Vietnamese input methods) — don't intercept.
      if (e.isComposing) return;

      if (!spec.allowInInput && isTypingTarget(e.target)) return;
      if (!spec.allowInModal && modalOpen()) return;

      handler(e);
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (t.isContentEditable) return true;
  return false;
}

function modalOpen(): boolean {
  return !!document.querySelector(".modal-backdrop");
}

/**
 * Render-friendly hint for the OS modifier. Use in tooltips and the
 * shortcuts cheatsheet.
 */
export const MOD_LABEL =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)
    ? "⌘"
    : "Ctrl";
